import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './MobileDocumentViewPage.css';
import { DocumentApi, DocumentMetadataApi, FileApi } from '../../services/ApiService';
import { DocumentEndeavorApi } from '../../services/api/DocumentEndeavorAPI';
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';

// The order you want for your fields
const fieldOrder = [
  "Endeavor",
  "Title",
  "Author",
  "Date",
  "Important Names & Events",
  "Important Names and Events",
  "Key Topics",
  "Summary",
  "Document Text",
  "Document Text Translated from English to Spanish",
  "Notes",
];

// Sort metadata by fieldOrder
function sortMetadata(metaArray) {
  const getIndex = (fieldName) => {
    const i = fieldOrder.findIndex(
      (f) => f.toLowerCase() === fieldName.toLowerCase()
    );
    return i === -1 ? 9999 : i;
  };

  return [...metaArray].sort((a, b) => {
    const nameA = a.metadata_definition?.name || a.metadataName || "";
    const nameB = b.metadata_definition?.name || b.metadataName || "";
    const iA = getIndex(nameA);
    const iB = getIndex(nameB);
    if (iA !== iB) return iA - iB;
    return nameA.localeCompare(nameB);
  });
}

// Custom splitting to avoid splitting "March 5, 1770"
function customSplitOnCommas(str) {
  return str
    .split(/,(?!\s*\d)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Parse as JSON array or fallback to custom comma split
function parseAsArray(str) {
  if (!str || typeof str !== "string") return [];
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (err) {
    // ignore error, fallback below
  }
  return customSplitOnCommas(str);
}

// Enhanced field value rendering
function renderFieldValue(fieldName, fieldValue) {
  if (!fieldValue) return <span className="m-field-empty">No value</span>;

  const lower = fieldName.toLowerCase();

  // If Key Topics or Important Names and Events, show as pill tags
  if (
    lower === "key topics" ||
    lower === "important names & events" ||
    lower === "important names and events"
  ) {
    const items = parseAsArray(fieldValue);
    return (
      <div className="m-tag-container">
        {items.map((tag, idx) => (
          <span key={idx} className="m-tag-pill">
            {tag}
          </span>
        ))}
      </div>
    );
  }

  // For long text fields, add proper formatting
  return (
    <div className="m-field-value">
      {fieldValue}
    </div>
  );
}

export default function MobileDocumentViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  console.log('MobileDocumentViewPage: Rendering with id:', id, 'user:', user);

  const [doc, setDoc] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [error, setError] = useState(null);
  const [associatedCourses, setAssociatedCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('metadata'); // 'metadata' or 'document'
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxConverting, setDocxConverting] = useState(false);

  // Refs for cleanup and management
  const fileUrlRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Proper mounting effect
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup function for blob URLs
  const cleanupFileUrl = useCallback(() => {
    if (fileUrlRef.current) {
      console.log('MobileDocumentView: Cleaning up blob URL');
      URL.revokeObjectURL(fileUrlRef.current);
      fileUrlRef.current = null;
      setFileUrl("");
    }
  }, []);

  // Load document and metadata
  useEffect(() => {
    console.log('MobileDocumentView: Document loading effect triggered for ID:', id);
    
    // Reset file-related state when document ID changes
    cleanupFileUrl();
    setFileUrl("");
    setFileError(null);
    setFileLoading(false);
    
    let abort = false;
    let timeoutId;

    const loadDocument = async () => {
      if (!id) {
        console.error('MobileDocumentView: No document ID provided');
        setError('No document ID provided');
        setLoading(false);
        return;
      }

      if (!user?.id && !user?.userId) {
        console.error('MobileDocumentView: No user ID available, user object:', user);
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      // Set a timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        if (!abort) {
          console.error('MobileDocumentView: Loading timeout after 30 seconds');
          setError('Loading timeout - please try again');
          setLoading(false);
        }
      }, 30000);

      try {
        setLoading(true);
        setError(null);
        console.log('MobileDocumentView: Loading document', id, 'for user', user.id || user.userId);
        
        // Load document - this is the main critical call
        const docResponse = await DocumentApi.getDocument(id);
        console.log('MobileDocumentView: Document API response:', docResponse.status, docResponse.ok);
        
        if (!docResponse.ok) {
          throw new Error(`Failed to load document: ${docResponse.status} ${docResponse.statusText}`);
        }
        const docData = await docResponse.json();
        console.log('MobileDocumentView: Document data:', docData);
        
        if (abort) return;
        setDoc(docData);

        // Load metadata - non-critical, continue on failure
        try {
          const metaResponse = await DocumentMetadataApi.getDocumentMetadata(id);
          if (metaResponse.ok) {
            const metaData = await metaResponse.json();
            if (!abort) {
              console.log('MobileDocumentView: Metadata loaded:', metaData);
              setMetadata(sortMetadata(metaData || []));
            }
          } else {
            console.warn('MobileDocumentView: Failed to load metadata:', metaResponse.status);
            setMetadata([]); // Set empty array so we don't stay in loading state
          }
        } catch (metaError) {
          console.error('MobileDocumentView: Error loading metadata:', metaError);
          setMetadata([]); // Set empty array so we don't stay in loading state
        }

        // Load associated courses - non-critical, continue on failure
        try {
          const courses = await DocumentEndeavorApi.getExistingEndeavorsForDocument(id, user.id || user.userId);
          if (!abort) {
            console.log('MobileDocumentView: Associated courses loaded:', courses);
            setAssociatedCourses(courses || []);
          }
        } catch (error) {
          console.error('MobileDocumentView: Error loading associated courses:', error);
          setAssociatedCourses([]); // Set empty array so we don't stay in loading state
        }

      } catch (error) {
        console.error('MobileDocumentView: Error loading document:', error);
        if (!abort) {
          setError(error.message || 'Failed to load document');
          setDoc(null);
          setMetadata([]);
        }
      } finally {
        if (!abort) {
          console.log('MobileDocumentView: Loading completed');
          setLoading(false);
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }
      }
    };

    loadDocument();
    return () => { 
      abort = true; 
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id, user?.userId]);

  // Enhanced file fetching with better error handling
  const fetchFileUrl = useCallback(async () => {
    console.log('MobileDocumentView: fetchFileUrl CALLED!');
    
    if (!doc) {
      console.log('MobileDocumentView: No document available for file fetch');
      return;
    }

    const documentId = doc.id || doc.documentId;
    if (!documentId) {
      console.error('MobileDocumentView: No document ID found in document object:', doc);
      setFileError('Document ID not found');
      return;
    }

    console.log('MobileDocumentView: Starting file fetch process for document ID:', documentId);

    console.log('MobileDocumentView: Checking if mounted:', isMountedRef.current);
    if (!isMountedRef.current) {
      console.log('MobileDocumentView: Component not mounted, aborting fetch');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      console.log('MobileDocumentView: Cancelling previous request');
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      console.log('MobileDocumentView: Fetching file URL for document ID:', documentId);
      setFileLoading(true);
      setFileError(null);
      
      // Clean up any existing URL first
      cleanupFileUrl();
      
      // First try to get a direct download URL (better for PDFs in iframes)
      try {
        console.log('MobileDocumentView: Trying to get direct download URL...');
        const urlResponse = await FileApi.getDownloadUrl(documentId, 7200); // 2 hour expiration
        console.log('MobileDocumentView: Direct URL response:', urlResponse.status, urlResponse.ok);
        
        if (urlResponse.ok) {
          const urlData = await urlResponse.json();
          console.log('MobileDocumentView: Got download URL response:', urlData);
          
          if (urlData.url || urlData.downloadUrl) {
            const directUrl = urlData.url || urlData.downloadUrl;
            console.log('MobileDocumentView: Using direct URL:', directUrl);
            setFileUrl(directUrl);
            return;
          }
        } else {
          console.warn('MobileDocumentView: Direct URL failed:', urlResponse.status);
        }
      } catch (urlError) {
        console.warn('MobileDocumentView: Direct URL method failed, trying blob download:', urlError);
      }
      
      // Fallback to blob download for inline viewing
      console.log('MobileDocumentView: Falling back to blob download for inline viewing...');
      const response = await FileApi.downloadFile(documentId);
      console.log('MobileDocumentView: File API response:', response.status, response.ok);
      
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        console.log('MobileDocumentView: Request was aborted');
        return;
      }
      
      if (!response.ok) {
        console.error('MobileDocumentView: File download failed with status:', response.status);
        throw new Error(`File download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('MobileDocumentView: File blob created, size:', blob.size, 'type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Get correct MIME type based on file extension (like desktop version)
      const getMimeType = (filename) => {
        if (!filename) return 'application/octet-stream';
        const ext = filename.toLowerCase().split('.').pop();
        const mimeTypes = {
          'pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'txt': 'text/plain',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        };
        return mimeTypes[ext] || 'application/octet-stream';
      };
      
      const correctMimeType = getMimeType(doc.name);
      const typedBlob = new Blob([blob], { type: correctMimeType });
      
      // Handle DOCX conversion for preview
      const isDocx = correctMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                     (doc.name && doc.name.toLowerCase().endsWith('.docx'));
      
      if (isDocx) {
        try {
          console.log('MobileDocumentView: Converting DOCX to HTML...');
          setDocxConverting(true);
          
          const arrayBuffer = await typedBlob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          if (result.value) {
            const sanitizedHtml = DOMPurify.sanitize(result.value, {
              ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'th', 'img', 'a', 'span', 'div'],
              ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class']
            });
            setDocxHtml(sanitizedHtml);
            console.log('MobileDocumentView: DOCX conversion successful');
            
            if (result.messages && result.messages.length > 0) {
              console.warn('MobileDocumentView: DOCX conversion warnings:', result.messages);
            }
          }
        } catch (conversionError) {
          console.error('MobileDocumentView: DOCX conversion failed:', conversionError);
          // Continue with normal blob URL rendering as fallback
        } finally {
          setDocxConverting(false);
        }
      }
      
      // Create blob URL for inline viewing
      const blobUrl = URL.createObjectURL(typedBlob);
      
      if (!isMountedRef.current) {
        URL.revokeObjectURL(blobUrl);
        return;
      }
      
      console.log('MobileDocumentView: Typed blob URL created for inline viewing:', {
        filename: doc.name,
        mimeType: correctMimeType,
        blobSize: typedBlob.size,
        url: blobUrl.substring(0, 50) + '...'
      });
      fileUrlRef.current = blobUrl;
      setFileUrl(blobUrl);
      
    } catch (error) {
      console.error('MobileDocumentView: Error fetching file:', error);
      console.error('MobileDocumentView: Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setFileError(error.message || 'Failed to load document file');
    } finally {
      console.log('MobileDocumentView: Fetch completed, setting loading to false');
      setFileLoading(false);
      abortControllerRef.current = null;
    }
  }, [doc, cleanupFileUrl]);

  // Load file when switching to document tab OR when document loads
  useEffect(() => {
    console.log('MobileDocumentView: Tab effect triggered', {
      activeTab,
      hasDoc: !!doc,
      docId: doc?.id || doc?.documentId,
      hasFileUrl: !!fileUrl,
      fileUrlValue: fileUrl,
      isFileLoading: fileLoading
    });
    
    if (activeTab === 'document' && doc && !fileUrl && !fileLoading) {
      console.log('MobileDocumentView: Auto-fetching file for document tab');
      console.log('MobileDocumentView: About to call fetchFileUrl...');
      fetchFileUrl();
    } else {
      console.log('MobileDocumentView: Not fetching file because:', {
        wrongTab: activeTab !== 'document',
        noDoc: !doc,
        alreadyHasUrl: !!fileUrl,
        fileUrlValue: fileUrl,
        currentlyLoading: fileLoading
      });
    }
  }, [activeTab, doc, fileUrl, fileLoading, fetchFileUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupFileUrl();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [cleanupFileUrl]);

  const handleEditClick = () => {
    navigate(`/m/document/${id}/edit`);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="m-doc-view-screen">
        <div className="m-doc-view-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Loading...</h1>
        </div>
        <div className="m-doc-view-loading">
          <div className="m-loading-spinner"></div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-doc-view-screen">
        <div className="m-doc-view-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="m-doc-view-error">
          <p>Error loading document: {error}</p>
          <button onClick={() => window.location.reload()} className="m-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="m-doc-view-screen">
        <div className="m-doc-view-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Document Not Found</h1>
        </div>
        <div className="m-doc-view-error">
          <p>The requested document could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="m-doc-view-screen">
      <div className="m-doc-view-header">
        <button className="m-back-btn" onClick={handleBackClick}>
          ← Back
        </button>
        <h1>{doc.name || 'Document'}</h1>
        <button className="m-edit-btn" onClick={handleEditClick}>
          Edit
        </button>
      </div>

      <div className="m-doc-view-tabs">
        <button 
          className={`m-tab-btn ${activeTab === 'metadata' ? 'active' : ''}`}
          onClick={() => setActiveTab('metadata')}
        >
          Metadata
        </button>
        <button 
          className={`m-tab-btn ${activeTab === 'document' ? 'active' : ''}`}
          onClick={() => setActiveTab('document')}
        >
          Document
        </button>
      </div>

      <div className="m-doc-view-content">
        {activeTab === 'metadata' && (
          <div className="m-metadata-section">
            {associatedCourses.length > 0 && (
              <div className="m-courses-section">
                <h3>Associated Courses</h3>
                <div className="m-courses-list">
                  {associatedCourses.map(course => (
                    <div key={course.id} className="m-course-tag">
                      {course.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="m-metadata-fields">
              {metadata.length === 0 ? (
                <div className="m-no-metadata">
                  <p>No metadata available for this document.</p>
                </div>
              ) : (
                metadata.map((field, index) => {
                  const fieldName = field.metadata_definition?.name || field.metadataName || 'Unknown Field';
                  const fieldValue = field.value || field.metadataValue || '';
                  
                  return (
                    <div key={index} className="m-metadata-field">
                      <div className="m-field-name">{fieldName}</div>
                      <div className="m-field-content">
                        {renderFieldValue(fieldName, fieldValue)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'document' && (
          <div className="m-document-section">
            {fileLoading || docxConverting ? (
              <div className="m-file-loading">
                <div className="m-loading-spinner"></div>
                <p>{docxConverting ? 'Converting document...' : 'Loading document...'}</p>
              </div>
            ) : fileError ? (
              <div className="m-file-error">
                <div className="error-icon">!</div>
                <div className="error-message">Failed to load document</div>
                <div className="error-suggestion">{fileError}</div>
                <button onClick={fetchFileUrl} className="m-retry-btn">
                  Try Again
                </button>
              </div>
            ) : docxHtml ? (
              <div className="m-document-viewer">
                <div 
                  className="docx-viewer"
                  dangerouslySetInnerHTML={{ __html: docxHtml }}
                  style={{
                    padding: '20px',
                    backgroundColor: 'var(--preview-iframe-bg, var(--semantic-surface-card, var(--surface-color)))',
                    color: 'var(--text-primary)',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    fontSize: '16px',
                    lineHeight: '1.6'
                  }}
                />
              </div>
            ) : doc.name && (doc.name.toLowerCase().endsWith('.pptx') || doc.name.toLowerCase().endsWith('.ppt')) ? (
              <div className="m-file-error">
                <div className="error-icon">📊</div>
                <div className="error-message">PowerPoint Preview</div>
                <div className="error-suggestion">PowerPoint preview is coming soon. Please download the file to view it.</div>
              </div>
            ) : fileUrl ? (
              <div className="m-document-viewer">
                <iframe
                  src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                  title={doc.name || 'Document'}
                  className="m-document-iframe"
                  onLoad={() => console.log('MobileDocumentView: Document loaded in iframe')}
                  onError={(e) => {
                    console.error('MobileDocumentView: Error loading document in iframe', e);
                    setFileError('Document preview failed to load');
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'var(--preview-iframe-bg, var(--semantic-surface-card, var(--surface-color)))'
                  }}
                />
              </div>
            ) : (
              <div className="m-no-document">
                <p>No document file available.</p>
                <button onClick={fetchFileUrl} className="m-retry-btn">
                  Load Document
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
